import { useCallback, useMemo, useState } from 'react';
import { readStored, storageKey, writeStored } from '@/lib/storage';

/*
  useColumnVisibility(tableId, columns) — which DataTable columns are shown, remembered per table in
  this browser (localStorage `sara.table.<tableId>.columns`). Pass the result to both the table and
  the toolbar's "Columns 7/15" menu:

    const columnState = useColumnVisibility('shop-drawings', columns);
    <ColumnsMenu state={columnState} />
    <DataTable columns={columns} columnVisibility={columnState} … />

  Column flags: `hideable` (default true; the FIRST column — the row's identity — defaults to false),
  `defaultHidden` (start hidden, e.g. Target date / PDF / DWG), `title` (menu label when `header` is not
  plain text). Columns without a label (an actions column) are not listed and not counted.
  A column added in a later release starts in its default state even for people who saved a choice.

  Returns { visibleColumns, isVisible(key), setVisible(key, bool), toggle(key), showAll(), reset(),
            menuColumns, visibleCount, totalCount, isCustomized }.
*/

export function columnLabel(column) {
  if (!column) return '';
  if (typeof column.title === 'string' && column.title) return column.title;
  if (typeof column.header === 'string') return column.header;
  return '';
}

export function isColumnHideable(column, index) {
  if (column.hideable === false) return false;
  if (column.hideable === true) return true;
  return index !== 0;
}

function defaultHidden(columns) {
  return columns.filter((column, index) => column.defaultHidden && isColumnHideable(column, index)).map((column) => column.key);
}

function readChoice(tableId) {
  if (!tableId) return null;
  const stored = readStored(storageKey('table', tableId, 'columns'), null);
  if (!stored || !Array.isArray(stored.hidden) || !Array.isArray(stored.known)) return null;
  return stored;
}

export function useColumnVisibility(tableId, columns = []) {
  const [choice, setChoice] = useState(() => readChoice(tableId));
  const columnKeys = columns.map((column) => column.key).join('|');

  const hidden = useMemo(() => {
    const set = new Set();
    columns.forEach((column, index) => {
      if (!isColumnHideable(column, index)) return;
      const known = choice && choice.known.includes(column.key);
      const isHidden = known ? choice.hidden.includes(column.key) : Boolean(column.defaultHidden);
      if (isHidden) set.add(column.key);
    });
    return set;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choice, columnKeys]);

  const persist = useCallback((nextHidden) => {
    const next = { hidden: [...nextHidden], known: columns.map((column) => column.key) };
    setChoice(next);
    if (tableId) writeStored(storageKey('table', tableId, 'columns'), next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, columnKeys]);

  const setVisible = useCallback((key, visible) => {
    const index = columns.findIndex((column) => column.key === key);
    if (index < 0 || !isColumnHideable(columns[index], index)) return;
    const next = new Set(hidden);
    if (visible) next.delete(key); else next.add(key);
    persist(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hidden, persist, columnKeys]);

  const toggle = useCallback((key) => setVisible(key, hidden.has(key)), [hidden, setVisible]);
  const showAll = useCallback(() => persist(new Set()), [persist]);
  const reset = useCallback(() => {
    setChoice(null);
    if (tableId) writeStored(storageKey('table', tableId, 'columns'), undefined);
  }, [tableId]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hidden.has(column.key)),
    [hidden, columns],
  );
  const menuColumns = columns
    .map((column, index) => ({ column, index, label: columnLabel(column) }))
    .filter((entry) => entry.label)
    .map((entry) => ({
      key: entry.column.key,
      label: entry.label,
      visible: !hidden.has(entry.column.key),
      hideable: isColumnHideable(entry.column, entry.index),
    }));
  const isCustomized = Boolean(choice) && (
    choice.hidden.length !== defaultHidden(columns).length
    || choice.hidden.some((key) => !defaultHidden(columns).includes(key))
  );

  return {
    visibleColumns,
    isVisible: (key) => !hidden.has(key),
    setVisible,
    toggle,
    showAll,
    reset,
    menuColumns,
    visibleCount: menuColumns.filter((entry) => entry.visible).length,
    totalCount: menuColumns.length,
    isCustomized,
  };
}

export default useColumnVisibility;
